/**
 * PublicShell — branded top-bar chrome for the *public* pages that live
 * outside <PrivateRoute>: /labels/verify and /trust/:siteId. Renders
 * HACCP AU branding so a phone-camera-from-the-pavement landing doesn't
 * look like a phishing site, and exposes the same layout primitives the
 * in-app surfaces use (eyebrow + title + subtitle + optional action).
 */
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck } from "lucide-react";

export default function PublicShell({
  title,
  subtitle,
  action,
  badge,
  badgeLabel = "Public audit trail",
  children,
  testid = "public-shell",
}) {
  return (
    <div className="min-h-screen bg-[color:var(--bg)]" data-testid={testid}>
      <header className="border-b border-[color:var(--border)] bg-white" data-testid="lv-public-header">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-eucalyptus-900 text-white flex items-center justify-center font-display font-extrabold">
              H
            </div>
            <div className="leading-tight">
              <div className="font-display font-extrabold text-base">HACCP AU</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-charcoal-soft">
                Food safety compliance
              </div>
            </div>
          </Link>
          {badge && (
            <Badge
              className="bg-eucalyptus-100 text-eucalyptus-900 hover:bg-eucalyptus-100 gap-1"
              data-testid="lv-public-badge"
            >
              <ShieldCheck className="w-3.5 h-3.5" /> {badgeLabel}
            </Badge>
          )}
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 py-6 animate-fade-in">
        {title && (
          <div className="mb-6">
            <div className="label-eyebrow">HACCP record</div>
            <div className="flex items-end justify-between gap-3 flex-wrap">
              <div>
                <h1 className="font-display font-extrabold text-2xl md:text-3xl">{title}</h1>
                {subtitle && (
                  <p className="text-sm text-charcoal-soft mt-1">{subtitle}</p>
                )}
              </div>
              {action}
            </div>
          </div>
        )}
        {children}
      </main>
      <footer className="max-w-5xl mx-auto px-4 py-8 text-center text-xs text-charcoal-soft">
        <Link to="/" className="hover:text-eucalyptus-900 underline-offset-4 hover:underline">
          Powered by HACCP AU — Audit-grade food safety
        </Link>
      </footer>
    </div>
  );
}
